// Game settings: one schema shared by the api, the room and the phones (spec GP-18).
// Each game type plays one rule set, tuned by the team and changed only in the park office: no host
// or player changes a rule (the game modes spec). The room stores the resolved values.
import { z } from 'zod';
import { DEFAULT_COMPETE, DEFAULT_HUSTLE, HUSTLE_EVENTS, hustleDay, hustleSchema, OPERATION_DISTRICTS, } from './compete.js';
export * from './leave.js';
/**
 * The race rings (packages/maps, one module each): third-mainland, the free Race's ring, then the
 * Grand Prix's oshodi-loop, lekki-sprint and apapa-port-run. A Saturday's Track of the Week is one of these.
 */
export const RACE_TRACKS = ['third-mainland', 'oshodi-loop', 'lekki-sprint', 'apapa-port-run'];
export const isRaceTrack = (s) => RACE_TRACKS.includes(s);
/** lagos-route: the Park's map. The rest are the race rings. */
/** The cities City Hustle drives (packages/maps lagos-city). */
export const CITY_MAPS = ['lagos-city'];
export const MAP_IDS = ['lagos-route', ...RACE_TRACKS, ...CITY_MAPS];
/**
 * classic: the plain game. powers: a power-up park (spec GP-24, D-33): the same rules, plus powers
 * picked up on the road. A free-park mode only: never in a stake park. race: 2 to 8 kekes, 3 laps of
 * the Third Mainland ring, no sides (the game modes spec, Race). hustle: City Hustle (COMPETE AND WIN
 * CASH): a working day in the city, net takings at the end, no sides.
 */
export const MODES = ['classic', 'powers', 'hide', 'race', 'hustle'];
/** Kill (knock) range presets, in map tiles. */
export const KILL_RANGE_TILES = { short: 1, normal: 1.5, long: 2.25 };
const ms = (min, max) => z.number().int().min(min).max(max);
/**
 * Hide and seek (spec GP-16b): the agbero is known and comes looking; drivers hide until the
 * survival clock runs out. No horn, no breakdown reports, no votes. Every lever is here, not in code.
 */
export const hideSchema = z.object({
    /** How long the drivers must last, from the start. */
    survivalMs: ms(60_000, 600_000),
    /** Each stop a living driver finishes takes this much off the clock. */
    stopCutMs: ms(0, 60_000),
    /** The agbero waits this long at the start before moving: the drivers' head start. */
    headStartMs: ms(0, 30_000),
    /** Shortcut escapes each driver may use in the round. */
    escapes: z.number().int().min(0).max(5),
    /** How long one escape lasts before the shortcut pushes the driver out. */
    escapeMs: ms(1_000, 15_000),
    /** The last stretch of the clock: the agbero sees further and drives faster. */
    finalMs: ms(0, 120_000),
    finalVision: z.number().min(1).max(3),
    finalSpeed: z.number().min(1).max(2),
});
// ------------------------------------------------------------ race (the game modes spec, Race)
const factor = (min, max) => z.number().min(min).max(max);
/** A pickup's relative odds in a draw (only their ratio counts). */
const chance = z.number().int().min(0).max(100);
const odds = z
    .strictObject({ boost: chance, oil: chance, cone: chance, nepa: chance, okada: chance, fuel: chance })
    .refine((o) => o.boost + o.oil + o.cone + o.nepa + o.okada + o.fuel > 0, 'One pickup needs a chance.');
/** The six race pickups (engine words; the screen says Boost, Oil, Go-slow cone, NEPA, Okada, Fuel can). */
export const RACE_PICKUPS = ['boost', 'oil', 'cone', 'nepa', 'okada', 'fuel'];
/**
 * Race (the game modes spec): every number the race plays by, so balance changes need no code.
 * Speeds are factors on the Park keke's top speed; fuel is in tank units (a full tank is `tank`);
 * times are ms.
 */
export const raceSchema = z
    .object({
    laps: z.number().int().min(1).max(10),
    /** Top speed, as a factor on the Park keke's. */
    topSpeed: factor(1, 2),
    /** Standstill to top speed, and top speed to a stop (rain makes the stop 30% longer). */
    accelMs: ms(500, 10_000),
    brakeMs: ms(300, 5_000),
    /** Steering, in degrees a second: standing still, and at top speed (it eases between). */
    steerSlow: z.number().int().min(30).max(720),
    steerFast: z.number().int().min(15).max(720),
    /** The tank, full at the start. */
    tank: z.number().int().min(10).max(1_000),
    /** Units a second: full throttle, coasting or braking, stopped. */
    burnFull: factor(0, 10),
    burnCoast: factor(0, 10),
    burnStopped: factor(0, 10),
    /** Burn factors: on a Boost, and in a slipstream. */
    boostBurn: factor(1, 5),
    slipstreamBurn: factor(0.1, 1),
    /** Slipstream: within this many keke lengths behind another, for slipstreamMs, gives this speed. */
    slipstreamLengths: factor(0.5, 5),
    slipstreamMs: ms(0, 5_000),
    slipstreamSpeed: factor(1, 1.5),
    /** Units a second at a pump; the pit lane's limit as a factor on top speed. */
    refuelPerSec: factor(1, 200),
    pitSpeed: factor(0.2, 1),
    /** The HUD's thresholds, in tank units: the gauge turns red under lowFuel, the callout fires under calloutFuel. */
    lowFuel: z.number().int().min(0).max(1_000),
    calloutFuel: z.number().int().min(0).max(1_000),
    /** Pickup boxes come back this long after one is taken. */
    pickupRespawnMs: ms(1_000, 60_000),
    /** The round ends this long after the winner crosses (or when every keke is done). */
    finishMs: ms(0, 120_000),
    /** Stuck or facing the wrong way this long: put back at the last gate crossed, held there resetHoldMs. */
    resetMs: ms(1_000, 10_000),
    resetHoldMs: ms(0, 5_000),
    /** The danfo lights, then Go; a keke that pushes off before Go is held this long after it. */
    countdownMs: ms(0, 10_000),
    earlyHoldMs: ms(0, 5_000),
    /** A warning pops up this long before a keke reaches an obstacle. */
    warnMs: ms(0, 5_000),
    /** At night obstacles show only within the headlamps, this many tiles (the danfo and the area boys still show). */
    headlampTiles: factor(1, 12),
    /** The pickups: what each does and for how long. */
    boostSpeed: factor(1, 2),
    boostMs: ms(500, 10_000),
    oilMs: ms(1_000, 60_000),
    coneMs: ms(1_000, 60_000),
    nepaMs: ms(1_000, 30_000),
    nepaVision: factor(0.1, 1),
    okadaMs: ms(500, 10_000),
    fuelCan: z.number().int().min(1).max(1_000),
    /** The pickup draw: everyone's table, and the kinder one for the last `kindPlaces` places. */
    pickupOdds: odds,
    kindOdds: odds,
    kindPlaces: z.number().int().min(0).max(7),
    /** A keke that crashes into something solid, or hits oil, is shaken: half speed this long. */
    shakenMs: ms(0, 5_000),
    shakenSpeed: factor(0.1, 1),
    /** The obstacles (the spec's table). */
    speedBump: z.strictObject({ over: factor(0, 1), flyMs: ms(0, 2_000) }),
    wheelbarrow: z.strictObject({ everyMs: ms(1_000, 60_000), crossMs: ms(500, 30_000), stopMs: ms(0, 5_000) }),
    /** An area boy on the ring: a crawl and a little fuel to settle him, unless you hold a paid ticket
     *  (ticket: the share of boxes, in percent, that give one instead of a pickup). */
    areaBoy: z.strictObject({
        crawl: factor(0.05, 1),
        crawlMs: ms(0, 5_000),
        fuel: z.number().min(0).max(100),
        ticket: z.number().int().min(0).max(100),
        /** Pass (no settling): the share, in percent, of times he bangs the keke (slowed bangMs) instead of letting it go. */
        bang: z.number().int().min(0).max(100).default(50),
        bangMs: ms(0, 10_000).default(2_500),
    }),
    pothole: z.strictObject({
        slow: factor(0.1, 1),
        slowMs: ms(0, 5_000),
        shakeAfter: z.number().int().min(1).max(9),
        shakeSteer: factor(0.1, 1),
    }),
    gutter: z.strictObject({ stuckMs: ms(0, 5_000) }),
    goSlow: z.strictObject({ everyMs: ms(1_000, 120_000), brakeMs: ms(500, 60_000) }),
    puddle: z.strictObject({ spinMs: ms(0, 3_000), rainSpins: z.number().int().min(1).max(4) }),
    goats: z.strictObject({ ms: ms(500, 20_000) }),
    /**
     * The fuel-can run: a keke out of fuel is not out of the race. Its driver gets out (speed: a
     * factor on the Park keke's speed), carries a can to the pit pumps, fills it there (fillMs),
     * runs back and pours it in (pourMs): `can` tank units. Within `reach` tiles of a pump or the
     * keke. A keke that runs into a driver on foot knocks them down for downMs.
     */
    foot: z
        .strictObject({
        speed: factor(0.2, 1.5),
        can: z.number().int().min(1).max(1_000),
        fillMs: ms(0, 10_000),
        pourMs: ms(0, 10_000),
        reach: factor(0.5, 3),
        downMs: ms(0, 10_000),
    })
        .default({ speed: 0.9, can: 50, fillMs: 2_500, pourMs: 2_000, reach: 1.2, downMs: 2_000 }),
    /**
     * The roadside fuel sellers (the jerrycan boys on the ring's sellers layer): a driver on foot may
     * fill the can at one instead of the pumps (within foot.reach), never a keke. Slower (fillMs) and
     * with less in it (`can` tank units), so the pit stays the better run when it is near.
     */
    seller: z
        .strictObject({ can: z.number().int().min(1).max(1_000), fillMs: ms(0, 10_000) })
        .default({ can: 35, fillMs: 3_500 }),
    /** The horn: once every cooldownMs; the keke ahead within reach tiles wobbles (slowed wobbleMs),
     *  and the goats, the go-slow or the wheelbarrow ahead within reach clear clearMs later. */
    horn: z
        .strictObject({
        cooldownMs: ms(1_000, 60_000),
        reach: factor(1, 12),
        wobbleMs: ms(0, 3_000),
        clearMs: ms(0, 5_000),
    })
        .default({ cooldownMs: 6_000, reach: 5, wobbleMs: 700, clearMs: 800 }),
    /** Dents: each crash dents the keke (up to max), each dent takes `slow` off its top speed; a pit
     *  stop knocks one out every fixMs while it stands at the pump. */
    damage: z
        .strictObject({ slow: factor(0, 0.3), max: z.number().int().min(0).max(5), fixMs: ms(0, 10_000) })
        .default({ slow: 0.06, max: 3, fixMs: 1_000 }),
    /** The round ends this long after Go whatever happens (everyone dry or on foot still ends). */
    capMs: ms(60_000, 1_800_000).default(600_000),
    /** Cash runs: the finish time to the ms where the keke met the line inside its tick, not the tick's end. */
    exactTime: z.boolean().optional(),
    /** Cash runs: bot trouble (the competition's level): challenges each real keke meets a lap, and the longest a bot may hold one. */
    trouble: z.strictObject({ perLap: z.number().int().min(0).max(10), holdMaxMs: ms(0, 10_000) }).optional(),
})
    .strict()
    .superRefine((r, ctx) => {
    const issues = [
        [r.steerFast > r.steerSlow, ['steerFast'], 'A keke steers less at speed.'],
        [r.calloutFuel > r.lowFuel, ['calloutFuel'], 'Below the red gauge.'],
        [r.lowFuel > r.tank, ['lowFuel'], 'Inside the tank.'],
        [r.wheelbarrow.crossMs > r.wheelbarrow.everyMs, ['wheelbarrow', 'crossMs'], 'Inside his wait.'],
        [r.goSlow.brakeMs > r.goSlow.everyMs, ['goSlow', 'brakeMs'], 'Inside its wave.'],
    ];
    for (const [when, path, message] of issues)
        if (when)
            ctx.addIssue({ code: 'custom', path, message });
});
/** The spec's launch values. */
export const DEFAULT_RACE = {
    laps: 3,
    topSpeed: 1.4,
    accelMs: 2_500,
    brakeMs: 1_200,
    steerSlow: 225,
    steerFast: 85,
    tank: 100,
    burnFull: 0.55,
    burnCoast: 0.15,
    burnStopped: 0,
    boostBurn: 2,
    slipstreamBurn: 0.8,
    slipstreamLengths: 2,
    slipstreamMs: 1_000,
    slipstreamSpeed: 1.1,
    refuelPerSec: 25,
    pitSpeed: 0.5,
    lowFuel: 25,
    calloutFuel: 10,
    pickupRespawnMs: 8_000,
    finishMs: 30_000,
    resetMs: 3_000,
    resetHoldMs: 2_000,
    countdownMs: 3_000,
    earlyHoldMs: 1_000,
    warnMs: 2_000,
    headlampTiles: 4,
    boostSpeed: 1.5,
    boostMs: 3_000,
    oilMs: 15_000,
    coneMs: 10_000,
    nepaMs: 5_000,
    nepaVision: 0.3,
    okadaMs: 4_000,
    fuelCan: 25,
    pickupOdds: { boost: 25, oil: 22, cone: 22, nepa: 13, okada: 13, fuel: 5 },
    kindOdds: { boost: 35, oil: 5, cone: 5, nepa: 10, okada: 25, fuel: 20 },
    kindPlaces: 2,
    shakenMs: 1_500,
    shakenSpeed: 0.5,
    speedBump: { over: 0.7, flyMs: 400 },
    wheelbarrow: { everyMs: 6_000, crossMs: 3_000, stopMs: 1_000 },
    areaBoy: { crawl: 0.3, crawlMs: 1_500, fuel: 5, ticket: 15, bang: 50, bangMs: 2_500 },
    pothole: { slow: 0.5, slowMs: 800, shakeAfter: 3, shakeSteer: 0.6 },
    gutter: { stuckMs: 1_500 },
    goSlow: { everyMs: 20_000, brakeMs: 4_000 },
    puddle: { spinMs: 600, rainSpins: 2 },
    goats: { ms: 3_000 },
    foot: { speed: 0.9, can: 50, fillMs: 2_500, pourMs: 2_000, reach: 1.2, downMs: 2_000 },
    seller: { can: 35, fillMs: 3_500 },
    horn: { cooldownMs: 6_000, reach: 5, wobbleMs: 700, clearMs: 800 },
    damage: { slow: 0.06, max: 3, fixMs: 1_000 },
    capMs: 600_000,
};
/**
 * Agberos and the purse (engine: area boys): touts the game runs, never players, on foot at the
 * bus stops and the bottlenecks, collecting the park levy. About half the players start a round
 * with a paid ticket; a keke without one that passes an agbero gets him latched on, slowing it,
 * until it pays the levy from its purse (and so gets a ticket). A VIO may send one after a driver
 * to pin them. Every lever is here, not in code. Distances are in tiles, speeds in world units per
 * tick (full keke speed is 22).
 */
export const areaBoysSchema = z
    .object({
    /** One agbero for every this many players, rounded up (2: four at 8 players), Razaq besides. */
    perPlayers: z.number().int().min(1).max(8),
    /** Each walks to a new post this often. */
    moveMs: ms(30_000, 300_000),
    /** The share of players who start the round with a paid ticket. */
    ticketShare: z.number().min(0).max(1),
    /** The levy, from the purse (coins; all of it if the purse holds less). Paying gets a ticket. */
    levy: z.number().int().min(0).max(100),
    /** A keke with an agbero on it moves at this share of its speed. */
    latchSpeed: z.number().min(0.1).max(1),
    /** He lets go of a keke that never pays after this long, and leaves it alone a while after. */
    latchMs: ms(5_000, 120_000),
    leaveAloneMs: ms(0, 180_000),
    /** Send the boys: how long he hunts the driver, how long he pins them (no paying him off), and the sends a VIO has. */
    sendMs: ms(5_000, 60_000),
    pinMs: ms(1_000, 30_000),
    sends: z.number().int().min(0).max(3),
    /** How close a keke must pass for him to latch on, in tiles. */
    radius: z.number().min(0.5).max(4),
    /** How fast an agbero walks between posts and after a keke. */
    walkSpeed: z.number().int().min(1).max(30),
    /** How far the commotion of a latched agbero is heard, in tiles. */
    bangRadius: z.number().min(1).max(30),
    /** Stopped this close to the park office, the purse banks, in tiles. */
    bankRadius: z.number().min(0.5).max(4),
    /** Passengers dropped at this many different stops since the last bank is a full route: it banks. */
    routeStops: z.number().int().min(2).max(14),
    /**
     * The end prize for each player on the winning side: about an average purse (the mean of every
     * player's fares this round, kept between min and max), or none.
     */
    endPrize: z.enum(['average', 'none']),
    endPrizeMin: z.number().int().min(0).max(1_000),
    endPrizeMax: z.number().int().min(0).max(1_000),
})
    .strict();
/**
 * Arrest (the VIO's knock): the driver caught plays Bribe or Bail, a jittery needle over three
 * zones (lawyer, sweet spot, wahala, left to right). The lawyer frees them, the sweet spot is a
 * bribe (half their stops undone), wahala clamps the keke and locks them in the Task Force Yard
 * until a driver cuts the padlock. Every lever is here, not in code.
 */
export const arrestSchema = z
    .object({
    /** How long the needle runs before the officer loses patience (no tap is wahala). */
    needleMs: ms(1_500, 10_000),
    /** The zones' widths, as shares of the bar (the rest is wahala). */
    lawyer: z.number().min(0).max(0.5),
    sweet: z.number().min(0).max(0.9),
    /** A bribe undoes this share of the driver's stops done (rounded up). */
    bribeShare: z.number().min(0).max(1),
    /** Cut the Padlock: how many cuts, each held this long at the yard gate. */
    cuts: z.number().int().min(1).max(6),
    cutMs: ms(500, 8_000),
    /** How close to the yard's gate a driver must stand to cut, in tiles. */
    yardRadius: z.number().min(0.5).max(4),
})
    .strict();
/** The tuned arrest levers: a short needle, mostly wahala, three cuts to break a driver out. */
export const ARREST = {
    needleMs: 4_000,
    lawyer: 0.05,
    sweet: 0.25,
    bribeShare: 0.5,
    cuts: 3,
    cutMs: 2_000,
    yardRadius: 1.6,
};
/** The tuned area boy levers (spec: Area boys). */
export const AREA_BOYS = {
    perPlayers: 2,
    moveMs: 90_000,
    ticketShare: 0.5,
    levy: 5,
    latchSpeed: 0.4,
    latchMs: 30_000,
    leaveAloneMs: 20_000,
    sendMs: 20_000,
    pinMs: 8_000,
    sends: 1,
    radius: 1.2,
    walkSpeed: 9,
    bangRadius: 10,
    bankRadius: 1.5,
    routeStops: 5,
    endPrize: 'average',
    endPrizeMin: 10,
    endPrizeMax: 120,
};
/**
 * Special roles (spec GP-17): a layer on the classic rules, all off unless a park turns one on.
 * Engine words here; the screen says Mechanic, Conductor, Spotter, Hailer, Area boy, Copy-copy
 * and Shadow. The first five are drivers' roles, the last two agberos'.
 */
export const SPECIAL_ROLES = [
    'scientist',
    'engineer',
    'tracker',
    'noisemaker',
    'guardian',
    'shapeshifter',
    'phantom',
];
/** The roles drawn from the agberos; the rest are drawn from the drivers. */
export const AGBERO_ROLES = ['shapeshifter', 'phantom'];
export const isAgberoRole = (r) => AGBERO_ROLES.includes(r);
/** One role's quota and levers. The role's own defaults (simulation roles.ts) fill what is absent. */
export const roleSlotSchema = z
    .object({
    /** How many seats may get it. */
    count: z.number().int().min(0).max(3),
    /** The chance, in percent, that each of those seats is dealt (a seeded draw). */
    chance: z.number().int().min(0).max(100),
    /** After a use, how long until the next. */
    cooldownMs: ms(5_000, 120_000).optional(),
    /** How long one use lasts. */
    durationMs: ms(1_000, 60_000).optional(),
    /** Uses in a round (absent: the role's default; most have no limit). */
    charges: z.number().int().min(1).max(9).optional(),
})
    .strict();
export const rolesSchema = z
    .object(Object.fromEntries(SPECIAL_ROLES.map((r) => [r, roleSlotSchema.optional()])))
    .strict();
/** Is any special role on in this config (a seat that may be dealt one)? */
export const rolesOn = (c) => SPECIAL_ROLES.some((r) => (c.roles?.[r]?.count ?? 0) > 0 && (c.roles?.[r]?.chance ?? 0) > 0);
// ------------------------------------------------------------ weather (the game modes spec)
/** The four weathers. Day is the plain road; the rest change vision or braking for everyone alike. */
export const WEATHER_KINDS = ['day', 'night', 'rain', 'harmattan'];
export const weatherSchema = z
    .object({
    /** Relative odds of each weather at the start of a round (only their ratio counts). */
    odds: z
        .object({
        day: z.number().min(0).max(100),
        night: z.number().min(0).max(100),
        rain: z.number().min(0).max(100),
        harmattan: z.number().min(0).max(100),
    })
        .strict(),
    /** From the second minute, each minute turns the weather with a 1 in this chance (0: never turns). */
    turnOneIn: z.number().int().min(0).max(100),
    /** A bought weather: the round plays it from the start and it never turns. */
    fixed: z.enum(WEATHER_KINDS).optional(),
})
    .strict()
    .refine((w) => w.odds.day + w.odds.night + w.odds.rain + w.odds.harmattan > 0, {
    message: 'At least one weather needs a chance.',
    path: ['odds'],
});
/**
 * Clear 4 in 9, Rain 3 in 9, Harmattan 2 in 9; a turn 1 minute in 8 from the second. Night is no
 * longer a weather: every round turns from day to evening to night on its own clock (dayCycle). The
 * night odds stay in the schema for saved settings and are never drawn.
 */
export const DEFAULT_WEATHER = { odds: { day: 4, night: 0, rain: 3, harmattan: 2 }, turnOneIn: 8 };
// ------------------------------------------------------------ the time of day (every game type)
/** Day, then evening, then night: every round of every game type walks through all three. */
export const TIMES_OF_DAY = ['day', 'evening', 'night'];
/**
 * How long a round stays in daylight, then in the evening, before night falls for the rest of it.
 * The clock counts the time on the road only: a meeting does not eat the day.
 */
export const dayCycleSchema = z
    .object({
    dayMs: z.number().int().min(10_000).max(1_800_000),
    eveningMs: z.number().int().min(5_000).max(900_000),
})
    .strict();
/** The Park and Power-ups: a round runs about five minutes on the road, night from 3:20. */
export const DAY_CYCLE = { dayMs: 120_000, eveningMs: 80_000 };
export const matchConfigSchema = z
    .object({
    mode: z.enum(MODES),
    mapId: z.enum(MAP_IDS),
    /** At least 4 in every mode but Race, which starts with 2 kekes (refined below). */
    minPlayers: z.number().int().min(2).max(15),
    maxPlayers: z.number().int().min(2).max(15),
    /** At least 1 in every mode but Race, which has no agberos at all (refined below). */
    impostorCount: z.number().int().min(0).max(3),
    /** Multiplier on the base speed. */
    playerSpeed: z.number().min(0.5).max(3),
    /** Vision radius in tiles. */
    crewVision: z.number().min(1).max(12),
    impostorVision: z.number().min(1).max(15),
    /** Crew vision is multiplied by this while the lights are out. */
    lightsOutFactor: z.number().min(0.1).max(1),
    killCooldownMs: ms(10_000, 60_000),
    killRange: z.enum(['short', 'normal', 'long']),
    meetingsPerPlayer: z.number().int().min(0).max(9),
    meetingCooldownMs: ms(0, 60_000),
    discussionMs: ms(0, 120_000),
    votingMs: ms(15_000, 300_000),
    revealMs: ms(2_000, 10_000),
    /**
     * The start hold: the round is dealt, then the whole park stands still this long while every
     * phone shows the role card and how the round ends, so reading it costs nobody road time. The
     * room starts the match clock this far ahead; 0 starts at once (a race has its own countdown).
     */
    introMs: ms(0, 10_000).optional(),
    anonymousVotes: z.boolean(),
    confirmEjections: z.boolean(),
    /**
     * When the route bar (the park's stop progress) moves on players' screens: live, only when a
     * meeting opens, or never. Hiding it takes away "the bar moved, so they're a driver" proof.
     */
    taskBar: z.enum(['always', 'meetings', 'never']),
    ghostsSeeRoles: z.boolean(),
    taskCounts: z.object({
        common: z.number().int().min(0).max(2),
        short: z.number().int().min(0).max(5),
        long: z.number().int().min(0).max(3),
    }),
    sabotageCooldownMs: ms(10_000, 120_000),
    criticalMs: ms(20_000, 120_000),
    meetingCancelsSabotage: z.boolean(),
    /** Hide and seek only (absent in every other mode, so their configs and hashes stay as they were). */
    hide: hideSchema.optional(),
    /** Special roles (GP-17): absent in every rule set, so their configs and hashes stay as they were. */
    roles: rolesSchema.optional(),
    /**
     * A park that seats fewer than this many plays with one agbero, from this many with
     * impostorCount (the room resolves it at the start with forSeats). Absent: always impostorCount.
     */
    secondAgberoAt: z.number().int().min(5).max(15).optional(),
    /** The round's weather odds (absent: DEFAULT_WEATHER), or a bought weather in `fixed`. */
    weather: weatherSchema.optional(),
    /** Day, evening, night on the road clock (absent: DAY_CYCLE). */
    dayCycle: dayCycleSchema.optional(),
    /** Area boys and the purse: absent where they never work (hide and seek, stake tables). */
    areaBoys: areaBoysSchema.optional(),
    /** Arrests (the VIO's knock, Bribe or Bail, the yard): absent where a knock is a plain catch (hide and seek, a race). */
    arrest: arrestSchema.optional(),
    /** Race only (absent in every other mode, so their configs and hashes stay as they were). */
    race: raceSchema.optional(),
    /** City Hustle only: its balance numbers, the Saturday's city events and the bots' trouble a game hour. */
    hustle: hustleSchema.optional(),
    cityDay: z
        .strictObject({
        events: z.array(z.enum(HUSTLE_EVENTS)).max(HUSTLE_EVENTS.length),
        operation: z.enum(OPERATION_DISTRICTS).optional(),
    })
        .optional(),
    hustleTrouble: z.strictObject({ perHour: z.number().int().min(0).max(10), holdMaxMs: ms(0, 10_000) }).optional(),
})
    .superRefine((c, ctx) => {
    // a race is 2 to 8 kekes with no agbero, roles or purse; every other type has 4 players and an agbero
    const race = c.mode === 'race';
    const hustle = c.mode === 'hustle';
    // a park has VIOs among the players, stops and meetings; a race and a hustle have none
    const park = !race && !hustle;
    const issues = [
        // a race runs on a race ring, a hustle in the city, every other type on the Lagos route
        [race !== isRaceTrack(c.mapId), ['mapId'], 'Not this game’s map.'],
        [hustle !== CITY_MAPS.includes(c.mapId), ['mapId'], 'Not this game’s map.'],
        [hustle && !c.hustle, ['hustle'], 'A hustle needs these.'],
        [!hustle && c.hustle !== undefined, ['hustle'], 'Only a hustle has these.'],
        [!hustle && (c.cityDay !== undefined || c.hustleTrouble !== undefined), ['cityDay'], 'Only a hustle has these.'],
        [
            hustle && (c.impostorCount !== 0 || c.secondAgberoAt !== undefined),
            ['impostorCount'],
            'The city VIO is no player.',
        ],
        [hustle && c.maxPlayers > 12, ['maxPlayers'], 'A shift takes 12 kekes.'],
        [hustle && rolesOn(c), ['roles'], 'No roles in a hustle.'],
        [hustle && (c.areaBoys !== undefined || c.arrest !== undefined), ['areaBoys'], 'The city has its own.'],
        [race && !c.race, ['race'], 'A race needs these.'],
        [!race && c.race !== undefined, ['race'], 'Only a race has these.'],
        [race && (c.impostorCount !== 0 || c.secondAgberoAt !== undefined), ['impostorCount'], 'A race has no VIOs.'],
        [race && c.maxPlayers > 8, ['maxPlayers'], 'The grid takes 8 kekes.'],
        [race && rolesOn(c), ['roles'], 'No roles in a race.'],
        // the area boys a race has stand on the road (two posts in its obstacles), never the purse's
        [race && c.areaBoys !== undefined, ['areaBoys'], 'Not in a race.'],
        [race && c.arrest !== undefined, ['arrest'], 'Not in a race.'],
        [c.mode === 'hide' && c.arrest !== undefined, ['arrest'], 'Hide and seek catches, it never arrests.'],
        [park && c.impostorCount < 1, ['impostorCount'], 'A park needs a VIO.'],
        [park && c.minPlayers < 4, ['minPlayers'], 'A park needs 4 players.'],
        [park && c.maxPlayers < 4, ['maxPlayers'], 'A park needs 4 players.'],
    ];
    for (const [when, path, message] of issues)
        if (when)
            ctx.addIssue({ code: 'custom', path, message });
    if (c.roles) {
        if (c.mode === 'hide' && rolesOn(c)) {
            ctx.addIssue({ code: 'custom', path: ['roles'], message: 'Hide and seek has no special roles.' });
        }
        // each side's quotas must fit the seats that side has
        const sides = [
            {
                agbero: false,
                room: c.maxPlayers - c.impostorCount,
                message: (n) => `Too many driver roles: only ${n} drivers in a full park.`,
            },
            {
                agbero: true,
                room: c.impostorCount,
                message: (n) => `Too many VIO roles: only ${n} VIO${n === 1 ? '' : 's'}.`,
            },
        ];
        for (const side of sides) {
            let total = 0;
            for (const r of SPECIAL_ROLES) {
                if (isAgberoRole(r) !== side.agbero)
                    continue;
                total += c.roles[r]?.count ?? 0;
                if (total > side.room) {
                    ctx.addIssue({ code: 'custom', path: ['roles', r, 'count'], message: side.message(side.room) });
                    break;
                }
            }
        }
    }
    if (c.areaBoys) {
        if (c.mode === 'hide')
            ctx.addIssue({ code: 'custom', path: ['areaBoys'], message: 'Hide and seek has no area boys.' });
        if (c.areaBoys.endPrizeMin > c.areaBoys.endPrizeMax)
            ctx.addIssue({
                code: 'custom',
                path: ['areaBoys', 'endPrizeMin'],
                message: 'The end prize floor cannot be above its ceiling.',
            });
    }
    if (c.arrest && c.arrest.lawyer + c.arrest.sweet > 1)
        ctx.addIssue({
            code: 'custom',
            path: ['arrest', 'sweet'],
            message: 'The lawyer and the sweet spot must fit the bar.',
        });
    if (c.mode === 'hide' && !c.hide) {
        ctx.addIssue({ code: 'custom', path: ['hide'], message: 'Hide and seek needs its clock settings.' });
    }
    if (c.hide && c.hide.finalMs >= c.hide.survivalMs) {
        ctx.addIssue({
            code: 'custom',
            path: ['hide', 'finalMs'],
            message: 'The final stretch must be shorter than the whole clock.',
        });
    }
    if (c.minPlayers > c.maxPlayers) {
        ctx.addIssue({
            code: 'custom',
            path: ['minPlayers'],
            message: 'Minimum players cannot be more than maximum players.',
        });
    }
    if (2 * c.impostorCount >= c.maxPlayers) {
        ctx.addIssue({
            code: 'custom',
            path: ['impostorCount'],
            message: `Too many VIOs for ${c.maxPlayers} players: VIOs must be fewer than half the park.`,
        });
    }
    if (c.secondAgberoAt !== undefined && 2 * c.impostorCount >= c.secondAgberoAt) {
        ctx.addIssue({
            code: 'custom',
            path: ['secondAgberoAt'],
            message: `With ${c.secondAgberoAt} players ${c.impostorCount} VIOs would start level with the drivers.`,
        });
    }
    // a park with secondAgberoAt starts small with one agbero
    if (2 * (c.secondAgberoAt !== undefined ? 1 : c.impostorCount) >= c.minPlayers) {
        ctx.addIssue({
            code: 'custom',
            path: ['impostorCount'],
            message: `With ${c.minPlayers} players the VIOs would start level with the drivers. Raise the minimum or lower the VIOs.`,
        });
    }
    if (park && c.taskCounts.common + c.taskCounts.short + c.taskCounts.long === 0) {
        ctx.addIssue({ code: 'custom', path: ['taskCounts'], message: 'Every driver needs at least one stop.' });
    }
    if (c.impostorVision < c.crewVision) {
        ctx.addIssue({
            code: 'custom',
            path: ['impostorVision'],
            message: 'Agbero vision cannot be shorter than driver vision.',
        });
    }
});
const base = {
    mode: 'classic',
    mapId: 'lagos-route',
    minPlayers: 4,
    maxPlayers: 10,
    impostorCount: 2,
    playerSpeed: 1,
    crewVision: 5,
    impostorVision: 7.5,
    lightsOutFactor: 0.2,
    killCooldownMs: 30_000,
    killRange: 'normal',
    meetingsPerPlayer: 1,
    meetingCooldownMs: 15_000,
    discussionMs: 30_000,
    votingMs: 60_000,
    revealMs: 5_000,
    introMs: 5_000,
    anonymousVotes: true,
    confirmEjections: false,
    taskBar: 'always',
    ghostsSeeRoles: true,
    taskCounts: { common: 1, short: 2, long: 1 },
    sabotageCooldownMs: 30_000,
    criticalMs: 45_000,
    meetingCancelsSabotage: true,
};
/**
 * Race (the game modes spec): 2 to 8 kekes, 3 laps of the Third Mainland ring, first over the line
 * wins. No agberos (impostorCount 0), stops, horns or meetings; the park fields are unused. Vision
 * is how far ahead a driver sees the road's obstacles; every keke is on the minimap.
 */
const RACE = {
    ...base,
    mode: 'race',
    mapId: 'third-mainland',
    minPlayers: 2,
    maxPlayers: 8,
    impostorCount: 0,
    introMs: 0,
    crewVision: 10,
    impostorVision: 10,
    meetingsPerPlayer: 0,
    taskCounts: { common: 0, short: 0, long: 0 },
    race: DEFAULT_RACE,
    // three laps run under three minutes: evening on the second lap, night for the run home
    dayCycle: { dayMs: 40_000, eveningMs: 30_000 },
};
/**
 * City Hustle (COMPETE AND WIN CASH): one working day in the city, net takings at the end. Up to 12
 * kekes (the stand-ins work the city too), no VIOs among them (the city's VIO is the game's), no
 * stops or meetings. The day runs 6am to 10pm on the shift clock: evening from 5pm, night from 8pm.
 */
const HUSTLE = {
    ...base,
    mode: 'hustle',
    mapId: 'lagos-city',
    minPlayers: 2,
    maxPlayers: 12,
    impostorCount: 0,
    introMs: 0,
    crewVision: 8,
    impostorVision: 8,
    meetingsPerPlayer: 0,
    taskCounts: { common: 0, short: 0, long: 0 },
    hustle: DEFAULT_HUSTLE,
    // 11 game hours of day (to 5pm), 3 of evening (to 8pm), night to the end
    dayCycle: { dayMs: (DEFAULT_HUSTLE.shiftMs * 11) / 16, eveningMs: (DEFAULT_HUSTLE.shiftMs * 3) / 16 },
};
/**
 * The rule sets, one per game type (the game modes spec), plus the stake table's. Tuned by the team;
 * a rule changes here or in the park office, never in a lobby.
 */
export const PRESETS = {
    /**
     * The Park: the usual game. One agbero under eight players, two from eight, so a small park and a
     * full one both stay close.
     */
    park: { ...base, minPlayers: 4, secondAgberoAt: 8, areaBoys: AREA_BOYS, arrest: ARREST },
    /** Power-ups (spec GP-24, D-33): the Park's rules with powers picked up on the road. Free parks only. */
    powers: { ...base, mode: 'powers', minPlayers: 4, secondAgberoAt: 8, areaBoys: AREA_BOYS, arrest: ARREST },
    /**
     * Hide and seek (spec GP-16b): one known agbero hunts, the drivers hide until the clock runs out.
     * No horn and no votes; stops cut the clock; a few shortcut escapes each; a final stretch where
     * the agbero sees further and drives faster. Free parks only.
     */
    hide: {
        ...base,
        mode: 'hide',
        minPlayers: 4,
        impostorCount: 1,
        killCooldownMs: 10_000,
        killRange: 'normal',
        meetingsPerPlayer: 0,
        crewVision: 5,
        impostorVision: 5,
        confirmEjections: true,
        taskCounts: { common: 1, short: 2, long: 1 },
        hide: {
            survivalMs: 180_000,
            stopCutMs: 10_000,
            headStartMs: 10_000,
            escapes: 2,
            escapeMs: 5_000,
            finalMs: 45_000,
            finalVision: 1.6,
            finalSpeed: 1.2,
        },
        // three minutes to survive: evening by the second minute, the final stretch in the dark
        dayCycle: { dayMs: 60_000, eveningMs: 50_000 },
    },
    /**
     * The 4-seat staked match (spec GP-18): one agbero, short timers, one horn each, verdicts on.
     * No area boys: a stake table plays for fare, not coins, so there is no purse or end prize for
     * them to reach and they never touch a stake.
     */
    stakes4p: {
        ...base,
        minPlayers: 4,
        maxPlayers: 4,
        impostorCount: 1,
        killCooldownMs: 20_000,
        meetingsPerPlayer: 1,
        discussionMs: 20_000,
        votingMs: 30_000,
        confirmEjections: true,
        anonymousVotes: false,
        taskCounts: { common: 1, short: 1, long: 0 },
        arrest: ARREST,
        // a short table: night from the third minute
        dayCycle: { dayMs: 80_000, eveningMs: 50_000 },
    },
    /** Race (the game modes spec): see RACE above. */
    race: RACE,
    /** City Hustle (COMPETE AND WIN CASH): see HUSTLE above. Cash runs only: never on the Play menu. */
    hustle: HUSTLE,
    /**
     * The Race stake table: the free Race's rules head to head, 2 kekes (STAKE_TABLES), 1st over the
     * line takes the pot (one winner, as Lagos Life pays). Off in live until Lagos Life accepts it.
     */
    stakesRace: { ...RACE, minPlayers: 2, maxPlayers: 2 },
};
/**
 * The cash-run pickups (COMPETE AND WIN CASH, the spec's "What changes from Race for a cash run"):
 * only the ones a keke uses on itself (Boost, Okada, Fuel can), nothing thrown at another player,
 * and the same table for every place, so no catch-up odds.
 */
const CASH_PICKUPS = { boost: 40, oil: 0, cone: 0, nepa: 0, okada: 35, fuel: 25 };
/**
 * The rule sets of the COMPETE AND WIN CASH games: never a free park, never a stake table. Keke
 * Grand Prix is the Race on the Track of the Week with the cash-run pickups (the Saturday's seed,
 * fixed weather and bot trouble come from the competition, not from here). City Hustle is a working
 * day in the city (HUSTLE above, its balance numbers DEFAULT_HUSTLE).
 */
export const COMPETE_PRESETS = {
    grandprix: {
        ...RACE,
        race: { ...DEFAULT_RACE, pickupOdds: CASH_PICKUPS, kindOdds: CASH_PICKUPS, kindPlaces: 0, exactTime: true },
    },
    hustle: HUSTLE,
};
/**
 * What each ring changes in a race's numbers (the Track data: laps, and a tank sized so a fast run
 * pits once or drives very carefully on the throttle). The rest is the game's own rule set. Third
 * Mainland is the ring the race presets are written for, so it changes nothing.
 */
export const TRACK_RULES = {
    'third-mainland': {},
    // four laps of about 50 s; full throttle all the way burns about 105 units
    'oshodi-loop': { laps: 4, tank: 90 },
    // three fast laps of about 55 s on the expressway: flat out burns about 95 units
    'lekki-sprint': { tank: 70 },
    // three long rough laps of about 70 s: flat out burns about 115 units (tanker boys sell by the kerb)
    'apapa-port-run': { tank: 85 },
};
/**
 * A race's rule set on a ring: its map and the ring's own numbers. The ring the rule set already
 * runs on with nothing to change gives it back as it is (so its replays and hashes stay put).
 */
export function onTrack(c, track) {
    const rules = TRACK_RULES[track];
    if (!c.race || (c.mapId === track && Object.keys(rules).length === 0))
        return c;
    return { ...c, mapId: track, race: { ...c.race, ...rules } };
}
/** The trouble a level means under these competition settings. */
export const troubleFor = (level, c = DEFAULT_COMPETE) => ({
    perLap: c.botTrouble.perLap[level],
    holdMaxMs: c.botTrouble.holdMaxMs,
});
/**
 * One cash run's rule set: the game's preset on the Saturday's track (the Track of the Week, Third
 * Mainland unless the park office chose another) with its weather fixed from the start (it never
 * turns), so every run that day meets the same road, and its bot trouble. The seed is the
 * Saturday's too, passed to createMatch by the worker.
 */
export function cashRunConfig(game, weather, trouble, track = 'third-mainland') {
    const p = onTrack(COMPETE_PRESETS[game], track);
    return {
        ...p,
        race: { ...p.race, trouble: typeof trouble === 'string' ? troubleFor(trouble) : { ...trouble } },
        weather: { odds: { ...DEFAULT_WEATHER.odds }, turnOneIn: 0, fixed: weather },
    };
}
/** The hustle trouble a level means under these competition settings. */
export const hustleTroubleFor = (level, c = DEFAULT_COMPETE) => ({
    perHour: c.botTrouble.perGameHour[level],
    holdMaxMs: c.botTrouble.holdMaxMs,
});
/**
 * One City Hustle cash run's rule set: the hustle preset with the Saturday's weather fixed from the
 * start, the Saturday's city events (drawn from its seed, so the hub announces the same ones), and
 * its bot trouble. The seed is the Saturday's too, passed to createMatch by the worker.
 */
export function cashHustleConfig(weather, trouble, seed) {
    const day = hustleDay(seed);
    return {
        ...COMPETE_PRESETS.hustle,
        hustle: { ...DEFAULT_HUSTLE },
        cityDay: { events: [...day.events], ...(day.operation ? { operation: day.operation } : {}) },
        hustleTrouble: typeof trouble === 'string' ? hustleTroubleFor(trouble) : { ...trouble },
        weather: { odds: { ...DEFAULT_WEATHER.odds }, turnOneIn: 0, fixed: weather },
    };
}
/**
 * The game types a free park plays, in Play menu order (the menu adds Solo before them and the
 * stake table after). Each is one rule set above. The stake table comes through Lagos Life only.
 */
export const GAME_TYPES = ['park', 'race', 'hide', 'powers'];
/** The free parks' rule sets: one per game type. */
export const FREE_PRESETS = GAME_TYPES;
export const isGameType = (s) => GAME_TYPES.includes(s);
/**
 * Rule sets that are gone (Beginner, Advanced and the rest) map to the game type that replaced them;
 * the Race stake table is a race.
 */
export function gameTypeOf(preset) {
    if (isGameType(preset) || preset === 'stakes4p')
        return preset;
    if (preset === 'stakesRace')
        return 'race';
    if (['beginner', 'classic', 'fast', 'advanced'].includes(preset))
        return 'park';
    return null;
}
/** The rules a round starts with for this many seated players (the room's one resolve). */
export function forSeats(c, seated) {
    if (c.secondAgberoAt === undefined || seated >= c.secondAgberoAt)
        return c;
    return { ...c, impostorCount: 1 };
}
/**
 * Stake tables (spec PF-07, PF-08): the fares a player may stake, in the world's game money
 * (never real money). Lagos Life caps one match at ₦100,000; ours stay far below. A stake table's
 * seats pick its game (STAKE_TABLES): 2 race head to head, 4 play The Park.
 */
export const STAKE_TIERS = [100, 200, 500, 1_000, 2_000];
/** The rule sets a stake park plays under: joined only through a Lagos Life match, one round each. */
export const STAKE_PRESETS = ['stakes4p', 'stakesRace'];
export const isStakePreset = (s) => STAKE_PRESETS.includes(s);
/** A stake table's size, as Lagos Life's match says it, and the rule set it plays. */
export const STAKE_TABLES = { 2: 'stakesRace', 4: 'stakes4p' };
export const stakeSeats = [2, 4];
/** The rule set a table of this many seats plays, or null for a size we don't run. */
export function stakePresetFor(seats) {
    return seats === 2 || seats === 4 ? STAKE_TABLES[seats] : null;
}
/**
 * How long a stake table may wait to fill, from its park opening (the lobby shows the countdown).
 * Then, in live, the phone leaves Lagos Life's match (its fare comes back) and offers a free round;
 * in staging solo players take the empty seats for a practice round that settles nothing.
 */
export const STAKE_FILL_MS = 180_000;
export const isStakeTier = (n) => STAKE_TIERS.includes(n);
/** Validate settings from any source. Errors are keyed by field path for the form. */
export function parseMatchConfig(input) {
    const r = matchConfigSchema.safeParse(input);
    if (r.success)
        return { ok: true, config: r.data };
    const errors = {};
    for (const issue of r.error.issues) {
        const key = issue.path.join('.') || '_';
        errors[key] ??= issue.message;
    }
    return { ok: false, errors };
}
// ------------------------------------------------------------ game type switches (the game modes spec)
/**
 * One game type's switches, set in the park office for incidents (no release): on the Play menu and
 * playable; Solo for it; its stake table. Off means the menu doesn't show it and the server refuses
 * to open or start it.
 */
export const typeSwitchSchema = z.strictObject({ on: z.boolean(), solo: z.boolean(), stake: z.boolean() });
/**
 * Every game type on, with Solo; the stake table only for the Park (a type's table turns on when
 * Lagos Life has reviewed it). Staging turns every table on in its economy config.
 */
export const DEFAULT_PLAY = {
    types: Object.fromEntries(GAME_TYPES.map((t) => [t, { on: true, solo: true, stake: t === 'park' }])),
    weather: DEFAULT_WEATHER,
};
/** A change to the switches as the park office publishes it: any type, any switch, the odds. */
export const playPatchSchema = z.strictObject({
    types: z
        .strictObject(Object.fromEntries(GAME_TYPES.map((t) => [t, typeSwitchSchema.partial().optional()])))
        .optional(),
    weather: weatherSchema.optional(),
});
/** The defaults with a patch on top. */
export function mergePlay(patch) {
    const types = { ...DEFAULT_PLAY.types };
    for (const t of GAME_TYPES) {
        const p = patch?.types?.[t];
        if (p)
            types[t] = { ...types[t], ...Object.fromEntries(Object.entries(p).filter(([, v]) => v !== undefined)) };
    }
    return { types, weather: patch?.weather ?? DEFAULT_WEATHER };
}
/** The game type a rule set belongs to (the stake tables play their type's table rules). */
export function typeOfRules(preset) {
    if (preset === 'stakes4p')
        return 'park';
    if (preset.startsWith('stakes')) {
        const t = preset.slice(6).toLowerCase();
        return isGameType(t) ? t : null;
    }
    const t = gameTypeOf(preset);
    return t === 'stakes4p' ? 'park' : t;
}
/**
 * The switches as a world plays them. The test world (staging, and Lagos Life's sandbox, where its
 * review plays) has every game type's stake table on; the live world follows the park office. The
 * api and the workers both read the switches through this, so a table the api binds can start.
 */
export function playForMode(play, mode) {
    if (mode === 'live')
        return play;
    const types = Object.fromEntries(Object.entries(play.types).map(([t, sw]) => [t, { ...sw, stake: true }]));
    return { ...play, types };
}
/** May a park of this rule set open and start now (its type on, and its table on for a stake park)? */
export function typeAllowed(play, preset) {
    const t = typeOfRules(preset);
    if (!t)
        return false;
    const sw = play.types[t];
    return sw.on && (!preset.startsWith('stakes') || sw.stake);
}
export * from './compete.js';
export * from './rankings.js';
//# sourceMappingURL=index.js.map